import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import Login from "./components/auth/Login";
import Signup from "./components/auth/Signup";
import Layout from "./components/sidebar/Layout";
import OnboardingSteps from "./components/auth/OnboardingSteps";
import ForgotPassword from "./components/auth/ForgotPassword";
import PasswordResetSuccess from "./components/auth/PasswordResetSuccess";
import NewPassword from "./components/auth/NewPassword";
import EmailVerification from "./components/auth/EmailVerification";
import WorkspaceReady from "./components/auth/WorkspaceReady";
import Dashboard from "./components/dashboard/Dashboard";
import AuthLayout from "./components/layout/AuthLayout";
import Transcripts from "./components/dashboard/Transcripts";
import Customers from "./components/dashboard/Customers";
import Settings from "./components/dashboard/Settings";
import GoogleCallback from "./pages/GoogleCallback";
import ProtectedRoute from "./components/ProtectedRoute";
import Opportunities from "./components/dashboard/Opportunities";
import Leads from "./components/dashboard/Leads";
import LeadDetail from "./components/dashboard/LeadDetail";
import MyLeads from "./components/dashboard/MyLeads";
import KanbanBoard from "./components/dashboard/Tickets";
import tokenManager from "./utils/tokenManager";
import Tasks from "./components/dashboard/Tasks";
import Accounts from "./components/dashboard/Accounts";
import Contacts from "./components/dashboard/Contacts";
import UsersTeams from "./components/dashboard/UsersTeams";
import Appointments from "./components/dashboard/Appointments";
import AnalyticsReports from "./components/dashboard/Analytics";
import TeamMemberSignup from "./components/auth/TeamMemberSignup";
import HubSpotCallback from "./pages/HubSpotCallback";
import CallSystemIntegration from "./components/dashboard/CallSystemIntegration";
import AppointmentCallData from "./components/dashboard/AppointmentCallData";
import ZohoCallbackPage from "./pages/ZohoCallback";
import Integrations from "./components/dashboard/Integrations";
import TeamCalendar from "./components/dashboard/TeamCalendar";
import AccountDetail from "./components/dashboard/AccountDetail";
import OpportunityDetail from "./components/dashboard/OpportunityDetail";
import Cases from "./components/dashboard/Cases";
import CaseDetail from "./components/dashboard/CaseDetail";
import Visits from "./components/dashboard/Visits";
import VisitDetail from "./components/dashboard/VisitDetail";
import Projects from "./components/dashboard/Projects";
import ProjectDetail from "./components/dashboard/ProjectDetail";
import Products from "./components/dashboard/Products";
import ProjectTemplates from "./components/dashboard/ProjectTemplates";
import ProjectTemplateDetail from "./components/dashboard/ProjectTemplateDetail";
import ProjectTaskDetail from "./components/dashboard/ProjectTaskDetail";
import SalesforceIntegration from "./components/dashboard/SalesforceIntegration";
import SalesforceCallback from "./pages/SalesforceCallback";
import EmailCallback from "./pages/EmailCallback";
import EmailConnection from "./components/dashboard/EmailConnection";
import SalesEngagement from "./components/dashboard/SalesEngagement";
import MyOpportunities from "./components/dashboard/MyOpportunities";
import Reports from "./components/dashboard/Reports";
import ReportBuilder from "./components/dashboard/ReportBuilder";
import ReportView from "./components/dashboard/ReportView";
import { ReportsProvider } from "./contexts/ReportContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import BroadbandInfoDetail from "./components/dashboard/BroadbandInfoDetail";
import AccountAssetDetail from "./components/dashboard/AccountAssetDetail";
import SettingsPage from "./components/dashboard/UserSettings";
import VisitNotifications from "./components/dashboard/VisitNotifications";
import VisitNotificationDetail from "./components/dashboard/VisitNotificationDetail";
import LeadCallDataDetail from "./components/dashboard/LeadCallDataDetail";
import LeadCallData from "./components/dashboard/LeadCallData";
import Files from "./components/dashboard/Files";
import ContactDetail from "./components/dashboard/ContactDetail";
import PortsOrderDetail from "./components/dashboard/PortsOrderDetail";
import CaseFeedItems from "./components/dashboard/CaseFeedItems";
import FeedItems from "./components/dashboard/FeedItems";
import TaskDetail from "./components/dashboard/TaskDetail";
import AppointmentDetail from "./components/dashboard/AppointmentDetail";
import TeamTaskDetail from "./components/dashboard/TeamTaskDetail";
import TeamTasks from "./components/dashboard/TeamTasks";
import SalesforceAttachments from "./components/dashboard/SalesforceAttachments";
import EmailTemplates from "./components/dashboard/EmailTemplates";
import PhoneSystemDetail from "./components/dashboard/PhoneSystemDetail";
import CleansedLeadDetail from "./components/dashboard/CleansedLeadDetail";
import CleansedLeads from "./components/dashboard/CleansedLeads";
import WebexIntegrationSettings from "./components/dashboard/WebexIntegrationSettings";
import WebexComplianceSettings from "./components/dashboard/WebexComplianceSettings";
import QuoteSystemSettings from "./components/dashboard/QuoteSystemSettings";
import QuotesAdmin from "./components/dashboard/QuotesAdmin";
import ServiceCatalog from "./components/dashboard/ServiceCatalog";
import PublicQuoteView from "./components/dashboard/PublicQuoteView";
import QuoteDetail from "./components/dashboard/QuoteDetail";
import TVDisplayPage from "./components/tv/TVDisplay";
import DashboardBuilder from "./components/tv/DashboardBuilder";
import TVPairingPage from "./components/tv/TVPairingPage";
import DashboardsPage from "./components/tv/Dashboard";
import MyAppointmentTargets from "./components/MyAppointmentTargets";
import MyPerformance from "./components/MyPerformance";
import BroadbandSearch from "./components/dashboard/BroadbandSearch";
import Installs from "./components/dashboard/Installs";
import Credits from "./components/dashboard/Credits";
import AllPages from "./components/dashboard/AllPages";

function App() {
  const isAuthenticated = tokenManager.isAuthenticated();
  return (
    <ThemeProvider>
      <ReportsProvider>
        <Router>
          <Routes>
            {/* ─── Public auth routes ──────────────────────────────── */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/onboarding/" element={<OnboardingSteps />} />
            <Route path="/workspace-ready" element={<WorkspaceReady />} />
            <Route path="/email-verification" element={<EmailVerification />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-success" element={<PasswordResetSuccess />} />
            <Route path="/new-password" element={<NewPassword />} />
            <Route path="/auth/google/callback" element={<GoogleCallback />} />
            <Route path="/invite/:token" element={<TeamMemberSignup />} />
            <Route path="/hubspot/callback" element={<HubSpotCallback />} />
            <Route
              path="/salesforce/callback"
              element={<SalesforceCallback />}
            />
            <Route
              path="/integrations/zoho/callback"
              element={<ZohoCallbackPage />}
            />
            <Route
              path="/settings/email/callback"
              element={<EmailCallback />}
            />
            <Route path="/quote/:token" element={<PublicQuoteView />} />

            {/* ─── Public TV routes (no login, no sidebar) ─────────── */}
            {/* The TV itself can't log in, so these must be reachable */}
            {/* anonymously. Auth is handled via shareToken / pairing code. */}
            <Route path="/tv/pair" element={<TVPairingPage />} />
            <Route path="/tv/:shareToken" element={<TVDisplayPage />} />

            {/* ─── Authenticated app ───────────────────────────────── */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="transcripts" element={<Transcripts />} />
              <Route path="opportunities" element={<Opportunities />} />
              <Route path="cases" element={<Cases />} />
              <Route
                path="/opportunities/:id"
                element={<OpportunityDetail />}
              />

              <Route path="tickets" element={<KanbanBoard />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="team-tasks" element={<TeamTasks />} />
              <Route path="accounts" element={<Accounts />} />
              <Route path="contacts" element={<Contacts />} />
              <Route path="leads" element={<Leads />} />
              <Route path="visits" element={<Visits />} />
              <Route path="projects" element={<Projects />} />
              <Route path="products" element={<Products />} />
              <Route path="webex" element={<WebexIntegrationSettings />} />
              <Route path="quote" element={<QuoteSystemSettings />} />
              <Route
                path="webex-compliance"
                element={<WebexComplianceSettings />}
              />
              <Route path="/quotes" element={<QuotesAdmin />} />
              <Route path="/quotes/services" element={<ServiceCatalog />} />
              <Route path="/quotes/:id" element={<QuoteDetail />} />
              <Route path="sales-attach" element={<SalesforceAttachments />} />
              <Route path="/leads/:id" element={<LeadDetail />} />
              <Route path="/cleansed-leads" element={<CleansedLeads />} />
              <Route
                path="/cleansed-leads/:id"
                element={<CleansedLeadDetail />}
              />
              <Route path="/project-templates" element={<ProjectTemplates />} />
              <Route path="/email-templates" element={<EmailTemplates />} />
              <Route
                path="/project-templates/:id"
                element={<ProjectTemplateDetail />}
              />
              <Route
                path="/project-tasks/:id"
                element={<ProjectTaskDetail />}
              />
              <Route path="/projects/:id" element={<ProjectDetail />} />
              <Route path="/visits/:id" element={<VisitDetail />} />
              <Route
                path="/visit-notifications"
                element={<VisitNotifications />}
              />
              <Route
                path="/visit-notifications/:id"
                element={<VisitNotificationDetail />}
              />
              <Route path="/lead-call-data" element={<LeadCallData />} />
              <Route
                path="/lead-call-data/:callId"
                element={<LeadCallDataDetail />}
              />
              <Route
                path="/accounts/:accountId/phone-systems/:phoneSystemId"
                element={<PhoneSystemDetail />}
              />
              <Route path="/case-feeds" element={<CaseFeedItems />} />
              <Route path="/feeds" element={<FeedItems />} />
              <Route path="/cases/:id" element={<CaseDetail />} />
              <Route path="/tasks/:id" element={<TaskDetail />} />
              <Route path="/team-tasks/:id" element={<TeamTaskDetail />} />
              <Route path="/contacts/:id" element={<ContactDetail />} />
              <Route path="/accounts/:id" element={<AccountDetail />} />
              <Route path="/my-leads" element={<MyLeads />} />
              <Route path="/files" element={<Files />} />
              <Route path="/my-opportunities" element={<MyOpportunities />} />
              <Route
                path="/integrations/salesforce"
                element={<SalesforceIntegration />}
              />
              <Route
                path="/integrations/genzi"
                element={<CallSystemIntegration />}
              />
              <Route path="users" element={<UsersTeams />} />
              <Route path="appointments" element={<Appointments />} />
              <Route path="appointments/:id" element={<AppointmentDetail />} />
              <Route
                path="appointment-data"
                element={<AppointmentCallData />}
              />
              <Route path="/team-calendar" element={<TeamCalendar />} />
              <Route path="analytics" element={<AnalyticsReports />} />
              <Route path="customers" element={<Customers />} />
              <Route
                path="support"
                element={<div className="p-6">Support Page</div>}
              />
              <Route path="/reports" element={<Reports />} />
              <Route path="/reports/new" element={<ReportBuilder />} />
              <Route path="/reports/:id" element={<ReportView />} />
              <Route path="/assets/:id" element={<AccountAssetDetail />} />
              <Route
                path="/project-tasks/:id"
                element={<ProjectTaskDetail />}
              />
              <Route
                path="/broadband-information/:id"
                element={<BroadbandInfoDetail />}
              />
              <Route path="/ports-order/:id" element={<PortsOrderDetail />} />
              <Route path="/reports/:id/edit" element={<ReportBuilder />} />
              <Route path="settings" element={<Settings />} />
              <Route path="installs" element={<Installs />} />
              <Route path="credits" element={<Credits />} />
              <Route path="all-pages" element={<AllPages />} />
              <Route path="user-settings" element={<SettingsPage />} />
              <Route path="settings/integrations" element={<Integrations />} />
              <Route path="settings/emails" element={<EmailConnection />} />
              <Route path="engagements" element={<SalesEngagement />} />

              {/* Authenticated TV management (admin side) */}
              <Route path="/dashboards" element={<DashboardsPage />} />
              <Route path="/my-targets" element={<MyAppointmentTargets />} />
              <Route path="/my-performance" element={<MyPerformance />} />
              <Route path="/broadband-search" element={<BroadbandSearch />} />
              <Route
                path="/dashboards/:id/builder"
                element={<DashboardBuilder />}
              />
            </Route>
          </Routes>
        </Router>
      </ReportsProvider>
    </ThemeProvider>
  );
}

export default App;
